import { useCallback, useRef, type ForwardedRef } from 'react'

function assignRef<T>(ref: ForwardedRef<T> | undefined, value: T | null) {
  if (!ref) return
  if (typeof ref === 'function') ref(value)
  else ref.current = value
}

/**
 * Letting a list that floats above a modal be scrolled AT ALL.
 *
 * Radix's Dialog locks scrolling while it is open, and `react-remove-scroll` does that by
 * listening for `touchmove` on `document` and cancelling anything that did not originate inside
 * the dialog's own DOM subtree. Every dropdown here is PORTALLED to `<body>` — that is what
 * stops it being clipped by the dialog — so as far as the lock is concerned a finger dragging
 * inside the dropdown is a finger dragging on the page behind it, and it is cancelled.
 *
 * BOTH `wheel` and `touchmove` are cancelled, and getting that wrong cost a release. The first
 * version of this handled `touchmove` alone, on the assumption — never measured — that the
 * wheel worked and only touchscreens were affected. It did not: a two-finger drag on a laptop
 * trackpad emits `wheel`, not touch events, and so does an ordinary mouse wheel. The list could
 * only be moved by dragging its scrollbar, on every device.
 *
 * Measured rather than assumed, this time. Inside the open list:
 *
 *     wheelPrevented: true      touchmovePrevented: false   (after the touch-only fix)
 *
 * So the scroll container stops both from reaching the document listener. The events still do
 * everything else they would: the browser scrolls the element natively, and
 * `overscroll-behavior: contain` in the class list is what stops the gesture chaining to the
 * page once the list hits its end. Nothing here calls `preventDefault`, which is why the
 * listeners can stay passive and the scroll stays smooth.
 *
 * A native listener rather than React's `onTouchMove`: React attaches its handlers to the root
 * container, and for content portalled out of that container the ordering against a
 * document-level listener is not something worth depending on. Attached to the element itself,
 * this always runs first.
 *
 * A CALLBACK ref rather than an object ref and an effect, which is the part that is easy to get
 * wrong and was: a dropdown's list does not exist until the dropdown opens, so an effect that
 * runs when the component mounts finds `ref.current` empty, attaches nothing, and never runs
 * again. A callback ref fires on every mount and unmount of the node itself, which is exactly
 * when the listener needs attaching and removing.
 */
export function useScrollIsolation<T extends HTMLElement>(forwardedRef?: ForwardedRef<T>) {
  const detach = useRef<(() => void) | null>(null)

  return useCallback((node: T | null) => {
    detach.current?.()
    detach.current = null
    assignRef(forwardedRef, node)
    if (!node) return

    const stop = (event: Event) => event.stopPropagation()
    // Passive: nothing here calls preventDefault, so the browser keeps its fast path and the
    // list scrolls at native speed.
    const opts = { passive: true } as const
    node.addEventListener('wheel', stop, opts)
    node.addEventListener('touchmove', stop, opts)
    detach.current = () => {
      node.removeEventListener('wheel', stop)
      node.removeEventListener('touchmove', stop)
    }
  }, [forwardedRef])
}
